import * as React from 'react'
import { useEffect } from 'react';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants'
import {
    Typography, Card, Grid, Button, Tooltip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import { useState } from 'react';
import { FormProvider, RHFSearchableSelectField, RHFSelectField, RHFTextField } from '../../common/hook-form';
import { LoadingButton } from '@mui/lab';
import { useForm, useFieldArray } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { decodeHtmlString } from '../../helpers/UtilityHelper';
import ViewHTMLPreview from './ViewPreview';
import AddEditEmailNotes from './AddEmailNotes';


function AddEditQCClickpostMapping({ setAddOpen, setReload, isEditData = null, courierList, setIsEditData }) {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmType, setConfirmType] = useState(null);
    const [showEmailNotes, setShowEmailNotes] = useState(false);
    const handleConfirm = (type) => {
        setConfirmOpen(true);
        setConfirmType(type);
    }


    const methods = useForm({
        defaultValues: {
            cpAccountCode: '',
            isActive: false,
            returnReasons: [{ returnReason: '', qcChecks: '' }],
            emailNotes: [{ noteFields: '' }]
        }
    });
    const { handleSubmit, reset, setValue, control } = methods;
    const { fields, append, remove } = useFieldArray({ control, name: 'returnReasons' });

    // set default values for edit data
    useEffect(() => {
        if (isEditData) {
            setValue('cpAccountCode', isEditData.cpAccountCode);
            setValue('isActive', isEditData.isActive);

            // Handle email notes - convert from string to array if needed
            if (isEditData.notes || isEditData.emailNotes) {
                const notesData = isEditData.notes || isEditData.emailNotes;
                let notesArray = [];

                if (typeof notesData === 'string' && notesData.trim()) {
                    // If it's a string, convert to array format
                    // Handle formatted notes (Note X: Content format)
                    const noteBlocks = notesData.split('\n\n').filter(block => block.trim());
                    notesArray = noteBlocks.map(block => {
                        const colonIndex = block.indexOf(':');
                        if (colonIndex > 0) {
                            const content = block.substring(colonIndex + 1).trim();
                            return { noteFields: content };
                        } else {
                            return { noteFields: block.trim() };
                        }
                    });
                } else if (Array.isArray(notesData) && notesData.length > 0) {
                    // If it's already an array, use it directly
                    notesArray = notesData.map(note => {
                        if (typeof note === 'string') {
                            return { noteFields: note };
                        } else {
                            return { noteFields: note.noteContent || note.content || note.noteFields || '' };
                        }
                    });
                }

                setValue('emailNotes', notesArray.length > 0 ? notesArray : [{ noteFields: '' }]);
                setShowEmailNotes(true);
            }

            if (isEditData.returnReasonChecks) {
                setValue('returnReasons', []);
                isEditData.returnReasonChecks.forEach((item) => {
                    const qcChecks = item.checks
                        .filter(check => Object.values(check)[0] === true)
                        .map(check => Object.keys(check)[0])
                        .join(', ');

                    const noQCChecks = item.checks
                        .filter(check => Object.values(check)[0] === false)
                        .map(check => Object.keys(check)[0])
                        .join(', ');

                    append({
                        returnReason: item.returnReason,
                        qcChecks,
                        noQCChecks
                    });
                });
            }
        }
    }, [isEditData, setValue, append, reset]);


    // construct payload for save or update
    const generateQCClickPostPayload = (data, courierList) => {
        const { cpId, id } = courierList.find(item => item.cpAccountCode === data.cpAccountCode);
        let processedNotes = [];
        if (data.emailNotes) {
            processedNotes = data.emailNotes.map(note => note.noteFields).filter(note => note && note.trim() !== '');
        }

        return {
            cpAccountCode: data.cpAccountCode,
            cpId,
            cpOid: id,
            isActive: true,
            notes: processedNotes,
            returnReasonChecks: (data.returnReasons || data.noQCChecks || []).map(item => ({
                returnReason: item.returnReason ? item.returnReason : 'NA',
                checks: [
                    ...(item.qcChecks
                        ? item.qcChecks.split(',').filter(Boolean).map(check => ({ [check.trim()]: true }))
                        : []),
                    ...(item.noQCChecks
                        ? item.noQCChecks.split(',').filter(Boolean).map(check => ({ [check.trim()]: false }))
                        : [])
                ]
            }))
        };
    };

    // generate preview
    const onSubmit = async (data) => {
        // setIsLoading(true);
        const payload = generateQCClickPostPayload(data, courierList);
        try {
            const response = await postGeneric(apiConstants.PREVIEW_QC_CLICKPOST_MAPPING, payload);
            if (response.status === 200) {
                const cleanedHtml = decodeHtmlString(response.data);
                setPreviewHtml(cleanedHtml);
                setPreviewOpen(true);
                enqueueSnackbar('Preview generated successfully', { variant: 'success' });
            }
        } catch (error) {
            enqueueSnackbar('Error generating preview', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // handle save after preview
    const handleSaveAfterPreview = async () => {
        const payload = generateQCClickPostPayload(methods.getValues(), courierList);
        setIsLoading(true);
        try {
            if (confirmType === 'save') {
                await postGeneric(apiConstants.SAVE_QC_CLICKPOST_MAPPING, {
                    ...payload,
                    isEmailSent: false,
                    isEmailSentManually: true
                });
            }

            if (confirmType === 'sendMail') {
                const mailResponse = await postGeneric(apiConstants.SAVE_AND_SEND_MAIL_TO_CLICKPOST, payload);
                if (mailResponse.status === 200) {
                    await postGeneric(apiConstants.SAVE_QC_CLICKPOST_MAPPING, {
                        ...payload,
                        isEmailSent: true,
                        isEmailSentManually: false
                    });
                }
            }
            enqueueSnackbar('Saved Successfully', { variant: 'success' });
            setPreviewOpen(false);
            setAddOpen(false);
            setReload(prev => prev + 1);
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Error saving data', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };
    const [qcReturnReasons, setQcReturnReasons] = useState([]);
    const courier = methods.watch('cpAccountCode');

    useEffect(() => {
        if (courier) {
            const prozoId = courierList.find(item => item.cpAccountCode === courier)?.prozoId;
            getGeneric(`${apiConstants.GET_COURIER_CONFIG_BY_PROZO_ID}/?prozoId=${prozoId}`).then((res) => {
                if (res?.data?.qcReturnReasons?.length > 0) {
                    setQcReturnReasons(res.data.qcReturnReasons);
                }else{
                    setQcReturnReasons([]);
                }
            });
        }
    }, [courier]);
    

    return (
        <>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RHFSelectField
                                width="100%"
                                name="cpAccountCode"
                                label="Select CP Account Code"
                                required
                                disabled={isEditData?.cpAccountCode}
                                options={_.orderBy(courierList, 'name').map(val => ({
                                    value: val.cpAccountCode,
                                    label: val.cpAccountCode
                                }))}
                            />
                        </Grid>

                        {/* <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <RHFCheckbox name="isActive" label="Active Status" labelPlacement="end" />
                        </Grid> */}

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                <AssignmentTurnedInIcon fontSize="small" sx={{ mr: 1 }} />
                                Return Reasons & QC Checks
                            </Typography>
                        </Grid>

                        <AddEditEmailNotes
                            control={control}
                            showEmailNotes={showEmailNotes}
                            setShowEmailNotes={setShowEmailNotes}
                        />

                        {fields.map((field, index) => (
                            <Grid item xs={12} key={field.id}>
                                <Card sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2, mb: 2 }}>
                                    <Grid container spacing={2} sx={{ mb:2 }}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                                Return Reason {index + 1}
                                                {fields.length > 1 && (
                                                    <IconButton size="small" color="error" onClick={() => remove(index)} sx={{ ml: 1 }}>
                                                        <DeleteForeverIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <RHFSearchableSelectField
                                                options={qcReturnReasons
                                                    .filter(item => {
                                                        const selectedReasons = fields.map(field => field.returnReason).filter(Boolean);
                                                        return !selectedReasons.includes(item) || item === field.returnReason;
                                                    })
                                                    .map(item => ({
                                                        value: item,
                                                        label: item
                                                    }))}
                                                name={`returnReasons.${index}.returnReason`}
                                                label="Return Reason"
                                                width="100%"
                                                variant="outlined"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4} sx={{mt:1}}>
                                            <RHFTextField
                                                name={`returnReasons.${index}.qcChecks`}
                                                required
                                                label={
                                                    <span>
                                                        QC Checks Performed{' '}
                                                        <Tooltip title="Comma separated list of QC checks that ARE performed (e.g., Image Verification, Brand Check)">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </Tooltip>
                                                    </span>
                                                }
                                                multiline
                                                placeholder="e.g., Image, Brand Check, Damage..."
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4} sx={{mt:1}}>
                                            <RHFTextField
                                                name={`returnReasons.${index}.noQCChecks`}
                                                label={
                                                    <span>
                                                        QC Checks NOT Performed{' '}
                                                        <Tooltip title="Comma separated list of QC checks that are NOT performed (e.g., Weight Check, Size Verification)">
                                                            <HelpOutlineIcon fontSize="small" />
                                                        </Tooltip>
                                                    </span>
                                                }
                                                multiline
                                                placeholder="e.g., Weight Check, SNo Check..."
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => append({ returnReason: '', qcChecks: '', noQCChecks: '' })}
                                sx={{ textTransform: 'none' }}
                            >
                                Add Another Return Reason
                            </Button>
                        </Grid>

                        <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
                            <LoadingButton
                                loading={isLoading}
                                variant="contained"
                                type="submit"
                                sx={{ minWidth: 120, textTransform: 'none' }}
                            >
                                Preview
                            </LoadingButton>
                            <Button
                                variant="outlined"
                                color="error"
                                sx={{ minWidth: 120, textTransform: 'none' }}
                                onClick={() => {
                                    reset();
                                    setValue('returnReasons', [{ returnReason: '', qcChecks: '' }]);
                                    setValue('emailNotes', [{ noteFields: '' }]);
                                    setShowEmailNotes(false);
                                }}
                            >
                                Reset
                            </Button>
                        </Grid>
                    </Grid>
                </Card>
            </FormProvider>
            <ViewHTMLPreview previewHtml={previewHtml} previewOpen={previewOpen}
                setPreviewOpen={setPreviewOpen} handleConfirm={handleConfirm}
                setIsEditData={setIsEditData} />
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Confirm Action
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {confirmType === 'save' ? (
                        <>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Save Configuration Only
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                • Configuration will be saved in Proship
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1, color: 'error.main', fontWeight: 'medium' }}>
                                • No email will be sent to Carrier
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Save & Send Email to Carrier
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                • Configuration will be saved in Proship
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1, color: 'success.main', fontWeight: 'medium' }}>
                                • Email with QC parameters will be sent to Carrier
                            </Typography>
                        </>
                    )}

                    <Typography variant="body2" sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, fontStyle: 'italic' }}>
                        Are you sure you want to proceed with this action?
                    </Typography>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        color="error"
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveAfterPreview}
                        color="primary"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                    >
                        Yes, Proceed
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default AddEditQCClickpostMapping;
